/**
 * Save a generated file (an export) to the user's disk.
 *
 * In the browser this triggers the standard blob-URL anchor download. In the
 * Tauri desktop shell, wry cancels blob-URL download navigations (notably on
 * WebKitGTK/Linux), so every export used to silently no-op — here we route the
 * bytes through the `export_save_file` Rust command, which atomically writes
 * them into the OS Downloads directory. Because the desktop shell shows no
 * download UI, the written path (or an error) is surfaced via a toast.
 *
 * Safe to call without `await`: the web path performs the anchor click
 * synchronously before its first `await`, so existing synchronous export
 * callers keep working unchanged; the Tauri path completes asynchronously and
 * handles its own success/error feedback (never rejects).
 *
 * @param {Blob} blob      the file contents
 * @param {string} filename suggested filename including extension
 * @returns {Promise<void>}
 */
import { showToast } from "./toast.js";
import { t } from "../../../shared/i18n/index.js";

export async function saveFileFromBlob(blob, filename, { onProgress, throwOnError = false } = {}) {
  const invoke =
    typeof window !== "undefined" && window.__TAURI_INTERNALS__
      ? window.__TAURI_INTERNALS__.invoke
      : null;

  if (!invoke) {
    onProgress?.({ label: t('vault.save.handing'), detail: t('vault.save.browserManages') });
    // Web: standard blob-URL download (runs synchronously — no await above).
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }
    return;
  }

  // Tauri desktop: write through Rust. Bytes go as a number array (map and
  // cache files cross raw since 2026-10-19, engine/storage/tauri-adapter.js).
  try {
    onProgress?.({ label: t('vault.save.saving') });
    const bytes = Array.from(new Uint8Array(await blob.arrayBuffer()));
    const path = await invoke("export_save_file", { filename, bytes });
    showToast(t('vault.save.savedTo', { path }), { type: "success" });
  } catch (err) {
    console.error("[Export] Tauri save failed:", err);
    showToast(t('vault.save.failed', { error: err }), { type: "error" });
    if (throwOnError) throw err;
  }
}
